import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  ShieldCheck,
  Eye,
  Scale,
  Activity,
  Users,
  Mountain,
  Coins,
  Landmark,
  CircleCheck,
  TrendingUp,
  FileText,
  Mic,
  Handshake,
  Building2,
  MessagesSquare,
} from 'lucide-react';
import { colors } from '../styles/colors';
import LiveReserveData from '../components/LiveReserveData';
import { useFillout } from '../hooks/useFillout';
import { BTX_REFERRAL_URL } from '../config/links';

// ---------- Shared SectionHeading (matches BTX/AZG aesthetic) ----------
const SectionHeading = ({ eyebrow, title, sub, align = 'center', maxW = 'max-w-3xl' }) => (
  <div className={`${maxW} ${align === 'center' ? 'mx-auto text-center' : ''} mb-12 sm:mb-16`}>
    {eyebrow && (
      <div className={`flex items-center gap-3 mb-4 ${align === 'center' ? 'justify-center' : ''}`}>
        <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />
        <span
          className="text-[11px] uppercase tracking-[0.28em] font-medium"
          style={{ color: colors.teal }}
        >
          {eyebrow}
        </span>
        {align === 'center' && <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />}
      </div>
    )}
    <h2
      className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight leading-tight"
      style={{ color: colors.white }}
    >
      {title}
    </h2>
    {sub && (
      <p
        className="text-base sm:text-lg leading-relaxed"
        style={{ color: colors.white, opacity: 0.65 }}
      >
        {sub}
      </p>
    )}
  </div>
);

const HomePage = () => {
  const [heroVisible, setHeroVisible] = useState(false);
  const navigate = useNavigate();
  const { openFilloutPopup } = useFillout();

  useEffect(() => {
    const raf = requestAnimationFrame(() => setHeroVisible(true));
    window.scrollTo(0, 0);
    return () => cancelAnimationFrame(raf);
  }, []);

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.pageYOffset - 96;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  // ----- Section 3: Framework principles -----
  const frameworkPrinciples = [
    { Icon: ShieldCheck, label: 'Verification' },
    { Icon: Eye, label: 'Transparency' },
    { Icon: Scale, label: 'Governance' },
    { Icon: Activity, label: 'Visibility' },
    { Icon: Users, label: 'Participation' },
  ];

  // ----- Section 4: Ecosystem participants -----
  const participants = [
    {
      Icon: Landmark,
      title: 'Liquidity Providers',
      body: 'Access transparent collateral frameworks and reserve visibility.',
      href: '/liquidity-providers',
      testid: 'ecosystem-card-liquidity-providers',
    },
    {
      Icon: Mountain,
      title: 'Mines',
      body: 'Explore alternative pathways to liquidity while maintaining ownership of underlying projects.',
      href: '/mines',
      testid: 'ecosystem-card-mines',
    },
    {
      Icon: Coins,
      title: 'Bullion Providers',
      body: 'Exchange physical bullion into the reserve and participate within a transparent ecosystem framework.',
      href: '/bullion-providers',
      testid: 'ecosystem-card-bullion-providers',
    },
  ];

  // ----- Section 5: How the Reserve Works -----
  const flowSteps = [
    { Icon: CircleCheck, label: 'Verification' },
    { Icon: Landmark, label: 'Reserve Participation' },
    { Icon: Eye, label: 'Reserve Visibility' },
    { Icon: TrendingUp, label: 'Ecosystem Interaction' },
    { Icon: FileText, label: 'Ongoing Reporting' },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#050505' }}>
      {/* ============================================================ */}
      {/* SECTION 1 — HERO                                              */}
      {/* ============================================================ */}
      <section
        id="overview"
        className="relative overflow-hidden"
        style={{
          minHeight: '82vh',
          background:
            'linear-gradient(180deg, #000000 0%, #050B0D 60%, #07141A 100%)',
        }}
      >
        {/* Hero asset — homepage centerpiece */}
        <picture>
          <source srcSet="/home-hero.webp" type="image/webp" />
          <img
            src="/home-hero.jpg"
            alt=""
            aria-hidden="true"
            data-testid="home-hero-image"
            className={`absolute inset-0 w-full h-full object-cover home-hero-drift transition-opacity duration-[2000ms] ease-out ${
              heroVisible ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ objectPosition: 'center center' }}
            loading="eager"
            decoding="async"
          />
        </picture>

        {/* Cinematic vignette overlays */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 35%, rgba(0,0,0,0.4) 65%, rgba(0,0,0,0.85) 100%)',
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(60% 70% at 50% 60%, rgba(0,128,128,0.10) 0%, rgba(0,0,0,0) 70%)',
          }}
        />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center text-center min-h-[82vh] pt-28 pb-16 sm:pt-28 sm:pb-20">
          <div
            className={`transition-all duration-1000 ease-out transform ${
              heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ fontFamily: "'Montserrat', system-ui, sans-serif" }}
          >
            {/* Eyebrow */}
            <div className="flex items-center gap-3 mb-7 justify-center">
              <span className="h-px w-10" style={{ backgroundColor: colors.teal, opacity: 0.7 }} />
              <span
                className="text-[11px] uppercase tracking-[0.32em] font-medium"
                style={{ color: colors.teal, fontFamily: 'inherit' }}
              >
                The Arizore Ecosystem
              </span>
              <span className="h-px w-10" style={{ backgroundColor: colors.teal, opacity: 0.7 }} />
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-[68px] leading-[1.05] tracking-[-0.02em] mb-7 max-w-4xl mx-auto"
              style={{ color: colors.white, fontFamily: 'inherit', fontWeight: 700 }}
            >
              TRANSFORMING VERIFIED ASSETS<br className="hidden sm:block" /> INTO USABLE LIQUIDITY.
            </h1>

            <p
              className="text-base sm:text-lg lg:text-xl leading-[1.55] max-w-2xl mx-auto mb-12"
              style={{
                color: 'rgba(255,255,255,0.72)',
                fontFamily: 'inherit',
                fontWeight: 400,
              }}
            >
              Connecting mines, bullion providers, and liquidity partners through a transparent reserve framework.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button
                onClick={() => scrollToId('ecosystem')}
                data-testid="home-hero-explore-ecosystem-btn"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
                style={{
                  backgroundColor: colors.teal,
                  color: colors.white,
                  border: `1px solid ${colors.teal}`,
                  fontFamily: 'inherit',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = `0 0 0 4px ${colors.teal}26`;
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Explore The Ecosystem
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes home-hero-drift {
            0%   { transform: scale(1.04) translate3d(0, 0, 0); }
            50%  { transform: scale(1.06) translate3d(-0.5%, -0.5%, 0); }
            100% { transform: scale(1.04) translate3d(0, 0, 0); }
          }
          .home-hero-drift { animation: home-hero-drift 24s ease-in-out infinite; will-change: transform; }
          @media (prefers-reduced-motion: reduce) {
            .home-hero-drift { animation: none; }
          }
        `}</style>
      </section>

      {/* ============================================================ */}
      {/* RESERVE SNAPSHOT — compact, reserve-centric, beneath hero     */}
      {/* ============================================================ */}
      <section
        id="reserve-snapshot"
        className="relative py-14 sm:py-20"
        style={{ backgroundColor: '#040404' }}
      >
        <LiveReserveData variant="compact" />
      </section>

      {/* ============================================================ */}
      {/* SECTION 2 — THE PROBLEM                                       */}
      {/* ============================================================ */}
      <section
        id="problem"
        className="relative py-20 sm:py-28 overflow-hidden"
        style={{ backgroundColor: '#040404' }}
      >
        {/* Background ambience using bg-grid asset (subtle, large, top-anchored) */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'url(/home-bg-grid.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center 20%',
            opacity: 0.12,
            filter: 'blur(1px)',
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, #040404 0%, rgba(4,4,4,0.6) 50%, #040404 100%)',
          }}
        />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="The Problem"
            title="VALUABLE ASSETS OFTEN STRUGGLE TO ACCESS LIQUIDITY."
            sub="Across the world, valuable assets remain underutilised within traditional financial systems."
          />

          {/* Three friction points + simple connector visual */}
          <div className="relative">
            <div className="grid md:grid-cols-3 gap-6 md:gap-7">
              {[
                {
                  Icon: Mountain,
                  title: 'Mining Projects',
                  body: 'Mining projects require capital to advance verified reserves through production stages.',
                },
                {
                  Icon: Coins,
                  title: 'Bullion Holdings',
                  body: 'Bullion holdings often lack broader utility beyond storage and physical transfer.',
                },
                {
                  Icon: Landmark,
                  title: 'Liquidity Providers',
                  body: 'Liquidity providers require transparent and verifiable collateral structures.',
                },
              ].map(({ Icon, title, body }) => (
                <div
                  key={title}
                  data-testid={`problem-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="relative p-7 sm:p-8 rounded-2xl text-center transition-all duration-500"
                  style={{
                    background:
                      'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  {/* Friction marker */}
                  <div
                    aria-hidden="true"
                    className="absolute top-4 right-4 w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: colors.goldMid, opacity: 0.45 }}
                  />
                  <div
                    className="mx-auto w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <Icon className="w-6 h-6" style={{ color: colors.teal }} strokeWidth={1.5} />
                  </div>
                  <h3
                    className="text-base font-semibold uppercase tracking-[0.12em] mb-3"
                    style={{ color: colors.white }}
                  >
                    {title}
                  </h3>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: colors.white, opacity: 0.65 }}
                  >
                    {body}
                  </p>
                </div>
              ))}
            </div>

            {/* Closing line */}
            <p
              className="mt-12 sm:mt-14 max-w-2xl mx-auto text-center text-base sm:text-lg leading-relaxed"
              style={{ color: colors.white, opacity: 0.75 }}
            >
              The challenge is not always asset quality. The challenge is creating{' '}
              <span style={{ color: colors.teal, fontWeight: 500 }}>
                trusted pathways between assets and capital
              </span>
              .
            </p>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 3 — THE SOLUTION                                      */}
      {/* ============================================================ */}
      <section
        id="solution"
        className="relative py-20 sm:py-28 overflow-hidden"
        style={{ backgroundColor: '#050505' }}
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'url(/home-bg-grid.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center bottom',
            opacity: 0.15,
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, #050505 0%, rgba(5,5,5,0.55) 50%, #050505 100%)',
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(50% 50% at 50% 50%, rgba(0,128,128,0.06) 0%, rgba(0,0,0,0) 70%)',
          }}
        />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="The Solution"
            title="A RESERVE FRAMEWORK DESIGNED TO CONNECT ASSETS AND CAPITAL."
            sub="Arizore brings together verified assets, reserve visibility, and ecosystem participants through a transparent reserve framework."
          />

          {/* Objective callout */}
          <div className="max-w-2xl mx-auto text-center mb-14 sm:mb-16">
            <p
              className="text-base sm:text-lg leading-relaxed"
              style={{ color: colors.white, opacity: 0.75 }}
            >
              The objective is simple: create{' '}
              <span style={{ color: colors.teal, fontWeight: 500 }}>structure, transparency, and participation pathways</span>{' '}
              around real-world assets.
            </p>
          </div>

          {/* 5 framework principles */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-14">
            {frameworkPrinciples.map(({ Icon, label }) => (
              <div
                key={label}
                data-testid={`solution-principle-${label.toLowerCase()}`}
                className="group flex flex-col items-center text-center p-6 rounded-xl transition-all duration-500"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
                  border: '1px solid rgba(255,255,255,0.07)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = `${colors.teal}45`;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = `0 12px 28px -16px ${colors.teal}40`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors duration-500"
                  style={{
                    backgroundColor: `${colors.teal}12`,
                    border: `1px solid ${colors.teal}30`,
                  }}
                >
                  <Icon className="w-6 h-6" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <span
                  className="text-xs sm:text-sm font-semibold uppercase tracking-[0.14em]"
                  style={{ color: colors.white }}
                >
                  {label}
                </span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="flex justify-center">
            <button
              onClick={() => navigate('/reserve-architecture')}
              data-testid="solution-cta-understand-reserve-framework"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg text-sm font-semibold uppercase tracking-[0.14em] transition-all duration-300"
              style={{
                backgroundColor: 'transparent',
                color: colors.teal,
                border: `1px solid ${colors.teal}`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${colors.teal}14`;
                e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.teal}1f`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Understand The Reserve Framework
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 4 — THE ECOSYSTEM                                     */}
      {/* ============================================================ */}
      <section
        id="ecosystem"
        className="py-20 sm:py-28"
        style={{ backgroundColor: '#040404' }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="rounded-3xl px-6 sm:px-10 lg:px-16 py-14 sm:py-16 lg:py-20"
            style={{
              background:
                'linear-gradient(180deg, rgba(255,255,255,0.025) 0%, rgba(255,255,255,0.01) 100%)',
              border: '1px solid rgba(255,255,255,0.06)',
              boxShadow:
                '0 30px 60px -30px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.04)',
            }}
          >
            <SectionHeading
              eyebrow="The Ecosystem"
              title="THREE PARTICIPANT GROUPS. ONE CONNECTED ECOSYSTEM."
              sub="The reserve framework brings independent participants into a single, transparent environment."
            />

            <div className="grid md:grid-cols-3 gap-6 lg:gap-7">
              {participants.map(({ Icon, title, body, href, testid }) => {
                const isInternal = href.startsWith('/');
                const handleClick = (e) => {
                  if (isInternal) {
                    e.preventDefault();
                    navigate(href);
                  }
                };
                return (
                  <a
                    key={title}
                    href={href}
                    onClick={handleClick}
                    data-testid={testid}
                    className="group relative flex flex-col p-7 sm:p-8 rounded-2xl h-full transition-all duration-500"
                    style={{
                      background:
                        'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      textDecoration: 'none',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = `${colors.teal}45`;
                      e.currentTarget.style.transform = 'translateY(-3px)';
                      e.currentTarget.style.boxShadow = `0 22px 44px -22px ${colors.teal}40`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                      style={{
                        backgroundColor: `${colors.teal}14`,
                        border: `1px solid ${colors.teal}40`,
                      }}
                    >
                      <Icon className="w-6 h-6" style={{ color: colors.teal }} strokeWidth={1.5} />
                    </div>
                    <h3
                      className="text-lg sm:text-xl font-semibold mb-3 leading-tight tracking-tight"
                      style={{ color: colors.white }}
                    >
                      {title}
                    </h3>
                    <p
                      className="text-sm sm:text-base leading-relaxed flex-grow mb-5"
                      style={{ color: colors.white, opacity: 0.65 }}
                    >
                      {body}
                    </p>
                    <span
                      className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] transition-all duration-300"
                      style={{ color: colors.teal }}
                    >
                      Learn More
                      <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 5 — HOW THE RESERVE WORKS                             */}
      {/* ============================================================ */}
      <section
        id="how-reserve-works"
        className="relative py-20 sm:py-28 overflow-hidden"
        style={{
          background:
            'linear-gradient(180deg, #050505 0%, #0A0A0A 50%, #050505 100%)',
        }}
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'url(/home-bg-grid.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            opacity: 0.1,
            transform: 'scaleY(-1)',
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(5,5,5,0.5) 0%, rgba(5,5,5,0.85) 60%, #050505 100%)',
          }}
        />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="How The Reserve Works"
            title="THE FRAMEWORK BEHIND THE ECOSYSTEM."
            sub="Assets enter the reserve through defined verification processes. The reserve provides visibility, governance, and reporting. Digital instruments serve as the interaction layer. The reserve remains the foundation."
          />

          {/* Vertical flow */}
          <div className="max-w-2xl mx-auto">
            <ol className="relative space-y-3">
              {/* Animated thread */}
              <div
                aria-hidden="true"
                className="absolute left-7 sm:left-8 top-7 bottom-7 w-px"
                style={{
                  background: `linear-gradient(180deg, transparent 0%, ${colors.teal}50 8%, ${colors.teal}50 92%, transparent 100%)`,
                }}
              />
              {flowSteps.map(({ Icon, label }, idx) => (
                <li
                  key={label}
                  data-testid={`flow-step-${idx + 1}`}
                  className="relative flex items-center gap-5 sm:gap-6 p-4 sm:p-5 rounded-xl transition-all duration-500"
                  style={{
                    background:
                      'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = `${colors.teal}35`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
                  }}
                >
                  {/* Step node */}
                  <div
                    className="relative z-10 flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center"
                    style={{
                      backgroundColor: '#070707',
                      border: `1px solid ${colors.teal}50`,
                      boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.03), 0 0 0 4px rgba(5,5,5,1)`,
                    }}
                  >
                    <Icon className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: colors.teal }} strokeWidth={1.5} />
                  </div>
                  <div className="flex items-center justify-between flex-grow gap-4">
                    <div>
                      <span
                        className="block text-[10px] uppercase tracking-[0.28em] font-medium mb-1"
                        style={{ color: colors.teal, opacity: 0.75 }}
                      >
                        Step {String(idx + 1).padStart(2, '0')}
                      </span>
                      <span
                        className="block text-base sm:text-lg font-semibold tracking-tight"
                        style={{ color: colors.white }}
                      >
                        {label}
                      </span>
                    </div>
                    <ArrowRight
                      aria-hidden="true"
                      className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 transition-transform duration-300"
                      style={{ color: colors.white, opacity: 0.25, transform: 'rotate(90deg)' }}
                    />
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* CTA */}
          <div className="mt-14 flex justify-center">
            <button
              onClick={() => navigate('/reserve-architecture')}
              data-testid="how-reserve-cta-architecture"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg text-sm font-semibold uppercase tracking-[0.14em] transition-all duration-300"
              style={{
                backgroundColor: 'transparent',
                color: colors.teal,
                border: `1px solid ${colors.teal}`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${colors.teal}14`;
                e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.teal}1f`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Explore Reserve Architecture
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Reserve snapshot relocated beneath hero (compact). */}

      {/* ============================================================ */}
      {/* SECTION 7 — RESERVE ROOM                                      */}
      {/* ============================================================ */}
      <section
        id="reserve-room-teaser"
        className="relative py-20 sm:py-28 overflow-hidden"
        style={{
          background:
            'linear-gradient(180deg, #050505 0%, #0A0A0A 50%, #050505 100%)',
        }}
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(60% 50% at 50% 50%, rgba(0,128,128,0.07) 0%, rgba(0,0,0,0) 70%)',
          }}
        />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* Reserve Room logo */}
            <div className="lg:col-span-5 flex justify-center lg:justify-start">
              <img
                src="/reserve-room-logo-white.png"
                alt="The Reserve Room"
                data-testid="home-reserve-room-logo"
                className="h-32 sm:h-40 lg:h-48 w-auto object-contain"
                style={{ opacity: 0.95 }}
                loading="lazy"
              />
            </div>

            {/* Copy + content list + CTA */}
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />
                <span
                  className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-medium"
                  style={{ color: colors.teal }}
                >
                  <Mic className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Reserve Room
                </span>
              </div>

              <h2
                className="text-3xl sm:text-4xl md:text-5xl font-bold mb-5 tracking-tight leading-tight"
                style={{ color: colors.white }}
              >
                CONVERSATIONS AROUND ASSETS, LIQUIDITY &amp; RESERVES.
              </h2>
              <p
                className="text-base sm:text-lg leading-relaxed mb-8 max-w-xl"
                style={{ color: colors.white, opacity: 0.65 }}
              >
                Explore interviews, industry insights, educational content, and ecosystem perspectives from across the reserve economy.
              </p>

              {/* Content categories */}
              <div className="grid sm:grid-cols-3 gap-3 mb-9">
                {[
                  { label: 'Latest Episode', sub: 'Podcast' },
                  { label: 'Featured Insight', sub: 'Research' },
                  { label: 'Recent Content', sub: 'Industry Perspectives' },
                ].map((item) => (
                  <div
                    key={item.label}
                    data-testid={`reserve-room-tile-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="p-4 rounded-xl"
                    style={{
                      background:
                        'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
                      border: '1px solid rgba(255,255,255,0.07)',
                    }}
                  >
                    <span
                      className="block text-[10px] uppercase tracking-[0.22em] font-medium mb-1.5"
                      style={{ color: colors.teal, opacity: 0.85 }}
                    >
                      {item.sub}
                    </span>
                    <span
                      className="block text-sm font-semibold tracking-tight"
                      style={{ color: colors.white }}
                    >
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => navigate('/reserve-room')}
                data-testid="reserve-room-cta-visit"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg text-sm font-semibold uppercase tracking-[0.14em] transition-all duration-300"
                style={{
                  backgroundColor: colors.teal,
                  color: colors.white,
                  border: `1px solid ${colors.teal}`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = `0 0 0 4px ${colors.teal}26`;
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Visit Reserve Room
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 9 — SUPPORTING THE ECOSYSTEM (trust signal)          */}
      {/* ============================================================ */}
      <section
        id="ecosystem-partners"
        className="py-20 sm:py-28"
        style={{ backgroundColor: '#050505' }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12">
            <div className="flex items-center gap-3 mb-4 justify-center">
              <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />
              <span className="text-[11px] uppercase tracking-[0.28em] font-medium" style={{ color: colors.teal }}>Partners</span>
              <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight" style={{ color: colors.white }}>
              SUPPORTING THE ECOSYSTEM.
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {[
              { src: '/partner-blacktie.png', alt: 'Black Tie', role: 'Black Tie', label: 'Tokenisation & Infrastructure Partner', url: 'https://www.blacktie.digital/' },
              { src: '/partner-planex.png', alt: 'Planex', role: 'Planex', label: 'Carbon & Environmental Markets Partner', url: 'https://planex.hk/' },
              { src: '/partner-gsd.png', alt: 'GSD', role: 'GSD', label: 'Mining Eligibility & Technical Review Partner', url: 'https://www.gsd.company/' },
            ].map(({ src, alt, role, label, url }) => (
              <a
                key={alt}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`partner-${role.toLowerCase().replace(/\s+/g, '-')}`}
                className="group p-5 sm:p-6 rounded-2xl flex flex-col items-center text-center transition-all duration-500"
                style={{
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  textDecoration: 'none',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${colors.teal}40`; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div className="h-10 sm:h-12 flex items-center justify-center mb-4 w-full">
                  <img
                    src={src}
                    alt={alt}
                    className="max-h-full max-w-full w-auto object-contain transition-opacity duration-500"
                    style={{ opacity: 0.8 }}
                    loading="lazy"
                  />
                </div>
                <span className="block text-sm font-semibold tracking-tight mb-1" style={{ color: colors.white }}>{role}</span>
                <span className="block text-[11px] leading-snug" style={{ color: colors.teal, opacity: 0.85 }}>{label}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 8 — MARKETPLACE ACCESS                                */}
      {/* ============================================================ */}
      <section
        id="marketplace-access"
        className="py-20 sm:py-28"
        style={{ backgroundColor: '#040404' }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="rounded-3xl px-6 sm:px-10 lg:px-16 py-14 sm:py-16 lg:py-20 text-center"
            style={{
              background:
                'linear-gradient(180deg, rgba(255,255,255,0.025) 0%, rgba(255,255,255,0.01) 100%)',
              border: '1px solid rgba(255,255,255,0.06)',
              boxShadow:
                '0 30px 60px -30px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.04)',
            }}
          >
            <SectionHeading
              eyebrow="Marketplace Access"
              title="MARKETPLACE ACCESS."
              sub="For participants seeking access to reserve-backed digital instruments, marketplace participation is available through BTX."
            />

            {/* Operational separation pair */}
            <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto mb-10">
              {[
                {
                  label: 'Arizore',
                  body: 'operates the reserve ecosystem.',
                  Icon: Landmark,
                },
                {
                  label: 'BTX',
                  body: 'operates the marketplace environment.',
                  Icon: Building2,
                },
              ].map(({ label, body, Icon }) => (
                <div
                  key={label}
                  data-testid={`marketplace-separation-${label.toLowerCase()}`}
                  className="p-6 rounded-xl text-left"
                  style={{
                    background:
                      'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
                    border: '1px solid rgba(255,255,255,0.07)',
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="flex-shrink-0 w-11 h-11 rounded-lg flex items-center justify-center"
                      style={{
                        backgroundColor: `${colors.teal}14`,
                        border: `1px solid ${colors.teal}40`,
                      }}
                    >
                      <Icon className="w-5 h-5" style={{ color: colors.teal }} strokeWidth={1.5} />
                    </div>
                    <p
                      className="text-sm sm:text-base leading-relaxed pt-0.5"
                      style={{ color: colors.white, opacity: 0.85 }}
                    >
                      <span className="font-semibold" style={{ color: colors.white, opacity: 1 }}>
                        {label}
                      </span>{' '}
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <a
              href={BTX_REFERRAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="marketplace-access-cta-continue-btx"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
              style={{
                backgroundColor: colors.teal,
                color: colors.white,
                border: `1px solid ${colors.teal}`,
                textDecoration: 'none',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = `0 0 0 4px ${colors.teal}26`;
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Continue To BTX Marketplace
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 10 — GET IN TOUCH                                     */}
      {/* ============================================================ */}
      <section
        id="get-in-touch"
        className="relative py-20 sm:py-28 overflow-hidden"
        style={{ backgroundColor: '#040404' }}
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'url(/home-bg-grid.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            opacity: 0.08,
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, #040404 0%, rgba(4,4,4,0.7) 50%, #040404 100%)',
          }}
        />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Get In Touch"
            title="EXPLORE PARTICIPATION OPPORTUNITIES."
            sub="Whether you are a liquidity provider, mining company, bullion provider, or strategic partner, our team can help you understand where you fit within the ecosystem."
          />

          {/* 4 participant routing cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {[
              {
                Icon: Landmark,
                title: 'Liquidity Providers',
                body: 'Discuss transparent collateral frameworks and reserve participation.',
                cta: 'Speak To Our Team',
                source: 'liquidity-providers',
              },
              {
                Icon: Mountain,
                title: 'Mines',
                body: 'Explore alternative liquidity pathways for mining projects.',
                cta: 'Speak To Our Team',
                source: 'mines',
              },
              {
                Icon: Coins,
                title: 'Bullion Providers',
                body: 'Integrate physical bullion into the reserve framework.',
                cta: 'Speak To Our Team',
                source: 'bullion-providers',
              },
              {
                Icon: Handshake,
                title: 'Strategic Partners',
                body: 'Build alongside the ecosystem through technical, infrastructure or institutional partnerships.',
                cta: 'Speak To Our Team',
                source: 'strategic-partners',
              },
            ].map(({ Icon, title, body, cta, source }) => (
              <div
                key={title}
                data-testid={`get-in-touch-card-${source}`}
                className="group relative flex flex-col p-6 sm:p-7 rounded-2xl h-full transition-all duration-500"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)',
                  border: '1px solid rgba(255,255,255,0.07)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = `${colors.teal}40`;
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = `0 20px 40px -20px ${colors.teal}40, inset 0 1px 0 rgba(255,255,255,0.05)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                  style={{
                    backgroundColor: `${colors.teal}14`,
                    border: `1px solid ${colors.teal}40`,
                  }}
                >
                  <Icon className="w-6 h-6" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <h3
                  className="text-base sm:text-lg font-semibold mb-3 leading-tight tracking-tight"
                  style={{ color: colors.white }}
                >
                  {title}
                </h3>
                <p
                  className="text-sm leading-relaxed flex-grow mb-6"
                  style={{ color: colors.white, opacity: 0.65 }}
                >
                  {body}
                </p>
                <button
                  onClick={openFilloutPopup}
                  data-testid={`get-in-touch-cta-${source}`}
                  className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] transition-all duration-300 self-start"
                  style={{
                    color: colors.teal,
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                  }}
                >
                  <MessagesSquare className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {cta}
                  <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
